import type { JSX, MemoExoticComponent } from 'react'
import type { SkinView } from '../../index.h'
import type { Scope } from '../core'

import { memo } from 'react'
import { key, useRender, useScope, useSources } from '../core'
import { hasLink, Link } from './Link'
import { hasMap, hasOver, Many } from './Many'
import { hasMatchCases, Match } from './Match'
import { One } from './One'

export type Recursive = typeof Recursive

interface Props {
  skin: SkinView
  parent?: Scope
  [key: string]: unknown // any additional props, will be propogated to ALL children
}

// exports memoized component
export const Recursive: MemoExoticComponent<typeof Recursive_> =
  memo(Recursive_)

// Recursive_.whyDidYouRender = true
Recursive_.displayName = 'Recursive'
function Recursive_({ skin, parent, ...props }: Props): JSX.Element | null {
  const sources: Scope = useSources(skin)
  const scope: Scope = useScope(skin, sources, parent)

  const render: boolean = useRender(skin, scope)
  if (!render) {
    return null
  }

  // skin has `map` or `over` field -> iterate over list values
  if (hasMap(skin) || hasOver(skin)) {
    return (
      <Many
        {...props}
        key={key(skin)}
        skin={skin}
        parent={scope}
        Recursive={Recursive}
      />
    )
  }

  // skin has `link` field -> render component, wrapped in link
  if (hasLink(skin)) {
    return (
      <Link
        {...props}
        key={key(skin)}
        skin={skin}
        parent={scope}
        Recursive={Recursive}
      />
    )
  }

  // skin has `match`/`cases` fields -> choose one of the cases
  if (hasMatchCases(skin)) {
    return (
      <Match
        {...props}
        key={key(skin)}
        skin={skin}
        parent={scope}
        Recursive={Recursive}
      />
    )
  }

  // default case -> render exact component
  return (
    <One
      {...props}
      key={key(skin)}
      skin={skin}
      parent={scope}
      Recursive={Recursive}
    />
  )
}
